import { useEffect, useMemo, useState } from 'react';

import { useDebouncedInputValue } from '../../../../hooks';
import { useSearchOrganizations } from '../../../../hooks/api/organization/useSearchOrganizations';
import { selectOptionFactory } from '../../../../utils';
import { Select } from '../../../generic/Select';
import type { SelectOption } from '../../../../types';

type Option = SelectOption<number>;

type Props = {
  selectedIds: number[];
  // Persisted titles for the currently selected org IDs (from `Banner.scopedOrganizations`),
  // so the chips render correctly when editing or duplicating before any search has run.
  initialOrganizations?: Array<{ id: number; title: string }>;
  isEnabled: boolean;
  onChange: (ids: number[]) => void;
};

/**
 * Multi-select picker for org-scoped banner target organizations.
 *
 * Reuses the super-user-only `useSearchOrganizations` hook. The query is enabled
 * only while the picker is enabled (org-scoped mode active for a super user); the
 * hook itself is also gated on `window.user?.super`.
 */
export const BannerOrganizationsPicker = ({ selectedIds, initialOrganizations, isEnabled, onChange }: Props) => {
  const { value: searchInput, onChange: onSearchChange, debouncedValue } = useDebouncedInputValue('');
  const query = debouncedValue.trim();

  // `isFetching` covers refetches as the debounced query changes; `isLoading` only fires
  // on the first request, which would leave the spinner off for subsequent searches.
  const { data: searchResults, isFetching } = useSearchOrganizations(query, isEnabled && query.length > 0);

  // Track every org we've seen (selected initialiser + anything the user has searched for) so chips
  // keep their labels even when later searches no longer include them.
  const [knownOrganizations, setKnownOrganizations] = useState<Record<number, Option>>(() => {
    const seed: Record<number, Option> = {};
    initialOrganizations?.forEach(org => {
      seed[org.id] = selectOptionFactory<number>(org.id, org.title);
    });
    return seed;
  });

  useEffect(() => {
    if (!searchResults || searchResults.length === 0) {
      return;
    }
    setKnownOrganizations(prev => {
      const next = { ...prev };
      searchResults.forEach(org => {
        next[org.id] = selectOptionFactory<number>(org.id, org.title);
      });
      return next;
    });
  }, [searchResults]);

  const selectedOptions = useMemo<Option[]>(() => (
    selectedIds.map(id => knownOrganizations[id] ?? selectOptionFactory<number>(id, `Org #${id}`))
  ), [selectedIds, knownOrganizations]);

  const dropdownOptions = useMemo<Option[]>(() => {
    if (!searchResults) {
      return [];
    }
    return searchResults.map(org => selectOptionFactory<number>(org.id, org.title));
  }, [searchResults]);

  return (
    <Select<Option, true>
      id="banner-organizations-picker"
      isMulti
      isDisabled={!isEnabled}
      isLoading={isFetching}
      placeholder="Search organizations by title or ID"
      noOptionsMessage={() => 'No organizations match'}
      inputValue={searchInput}
      onInputChange={(value, meta) => {
        // Preserve input across blur/menu close so the user's typed search is not lost.
        if (meta.action === 'input-change') {
          onSearchChange(value);
        }
      }}
      value={selectedOptions}
      options={dropdownOptions}
      onChange={(selected) => {
        onChange(selected.map(opt => opt.value));
        // Clear the search query after a selection so the input doesn't keep stale text.
        onSearchChange('');
      }}
      filterOption={null}
      closeMenuOnSelect={false}
    />
  );
};
